import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Activity, 
  RefreshCw, 
  User, 
  Clock, 
  ShoppingCart,
  Package,
  Users,
  Warehouse,
  FileText,
  Settings,
  Search,
  Filter,
  Loader2,
  Circle,
  CheckCircle2,
  AlertCircle,
  Edit,
  Trash2,
  Plus,
  Send,
  Download
} from "lucide-react";

// Action type icons and colors
const ACTION_CONFIG = {
  'order_created': { icon: ShoppingCart, color: 'bg-green-100 text-green-700', label: 'Order Created' },
  'order_updated': { icon: Edit, color: 'bg-blue-100 text-blue-700', label: 'Order Updated' },
  'order_deleted': { icon: Trash2, color: 'bg-red-100 text-red-700', label: 'Order Deleted' },
  'order_to_sort': { icon: Warehouse, color: 'bg-amber-100 text-amber-700', label: 'Sent to Sort' },
  'order_sorted': { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700', label: 'Order Sorted' },
  'order_dispatched': { icon: Send, color: 'bg-purple-100 text-purple-700', label: 'Order Dispatched' },
  'stock_updated': { icon: Package, color: 'bg-cyan-100 text-cyan-700', label: 'Stock Updated' },
  'stock_synced': { icon: RefreshCw, color: 'bg-indigo-100 text-indigo-700', label: 'Stock Synced' },
  'customer_created': { icon: Users, color: 'bg-teal-100 text-teal-700', label: 'Customer Created' },
  'customer_updated': { icon: Edit, color: 'bg-sky-100 text-sky-700', label: 'Customer Updated' },
  'customer_deleted': { icon: Trash2, color: 'bg-red-100 text-red-700', label: 'Customer Deleted' },
  'product_created': { icon: Plus, color: 'bg-lime-100 text-lime-700', label: 'Product Created' },
  'product_updated': { icon: Edit, color: 'bg-orange-100 text-orange-700', label: 'Product Updated' },
  'product_deleted': { icon: Trash2, color: 'bg-red-100 text-red-700', label: 'Product Deleted' },
  'user_login': { icon: User, color: 'bg-violet-100 text-violet-700', label: 'User Login' },
  'user_logout': { icon: User, color: 'bg-gray-100 text-gray-700', label: 'User Logout' },
  'settings_updated': { icon: Settings, color: 'bg-slate-100 text-slate-700', label: 'Settings Updated' },
  'report_generated': { icon: FileText, color: 'bg-pink-100 text-pink-700', label: 'Report Generated' },
  'pdf_downloaded': { icon: Download, color: 'bg-rose-100 text-rose-700', label: 'PDF Downloaded' },
  'default': { icon: Activity, color: 'bg-gray-100 text-gray-700', label: 'Activity' }
};

export default function ActivityLog() {
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUser, setFilterUser] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const refreshInterval = useRef(null);

  useEffect(() => {
    fetchActivities();
    fetchUsers();
    
    // Auto-refresh every 10 seconds
    if (autoRefresh) {
      refreshInterval.current = setInterval(fetchActivities, 10000);
    }
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh]);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activity-log`);
      setActivities(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
    }
  };

  const getActionConfig = (actionType) => {
    return ACTION_CONFIG[actionType] || ACTION_CONFIG['default'];
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/ /g, '-');
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = searchQuery === "" || 
      activity.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.details?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesUser = filterUser === "all" || activity.username === filterUser;
    const matchesAction = filterAction === "all" || activity.action_type === filterAction;
    
    return matchesSearch && matchesUser && matchesAction;
  });

  // Get unique action types from activities
  const uniqueActionTypes = [...new Set(activities.map(a => a.action_type))];

  // Group activities by date
  const groupedActivities = filteredActivities.reduce((groups, activity) => {
    const date = formatDateDDMonYY(activity.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(activity);
    return groups;
  }, {});

  return (
    <div className="p-6" data-testid="activity-log-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Activity className="w-7 h-7 text-primary" />
            Activity Log
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time tracking of all user actions
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchActivities}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* User filter */}
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-[180px]">
              <User className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map(user => (
                <SelectItem key={user.id} value={user.username}>
                  {user.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Action filter */}
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {uniqueActionTypes.map(actionType => {
                const config = getActionConfig(actionType);
                return (
                  <SelectItem key={actionType} value={actionType}>
                    {config.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Active filters indicator */}
          {(searchQuery || filterUser !== "all" || filterAction !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setFilterUser("all");
                setFilterAction("all");
              }}
              className="text-muted-foreground"
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* Activity Timeline */}
      <Card className="overflow-hidden">
        <ScrollArea className="h-[calc(100vh-320px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <Activity className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No activities found</p>
              <p className="text-sm">Activities will appear here as users perform actions</p>
            </div>
          ) : (
            <div className="p-4">
              {Object.entries(groupedActivities).map(([date, dayActivities]) => (
                <div key={date} className="mb-6">
                  {/* Date header */}
                  <div className="sticky top-0 bg-background/95 backdrop-blur z-10 py-2 mb-3">
                    <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {date}
                    </h3>
                  </div>

                  {/* Activities for this date */}
                  <div className="space-y-3 pl-4 border-l-2 border-border">
                    {dayActivities.map((activity, idx) => {
                      const config = getActionConfig(activity.action_type);
                      const IconComponent = config.icon;
                      
                      return (
                        <div 
                          key={activity.id || idx}
                          className="relative flex items-start gap-4 p-3 rounded-lg hover:bg-secondary/50 transition-colors group"
                        >
                          {/* Timeline dot */}
                          <div className="absolute -left-[1.35rem] top-5 w-3 h-3 rounded-full bg-background border-2 border-primary" />
                          
                          {/* Icon */}
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                            <IconComponent className="w-5 h-5" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground">
                                {activity.username}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {config.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-foreground">
                              {activity.description}
                            </p>
                            {activity.details && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {activity.details}
                              </p>
                            )}
                          </div>

                          {/* Timestamp */}
                          <div className="flex-shrink-0 text-xs text-muted-foreground">
                            {formatTimestamp(activity.timestamp)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* Live indicator */}
      {autoRefresh && (
        <div className="fixed bottom-6 right-6">
          <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-2 rounded-full shadow-lg">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
            </span>
            <span className="text-sm font-medium">Live Updates</span>
          </div>
        </div>
      )}
    </div>
  );
}
